import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import Rules from './Rules';
import './Login.css';

interface Team {
  id: string;
  name: string;
  passcode: string;
  teamPicture?: string;
}

const Login: React.FC = () => {
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const { login } = useAuth();

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoadingTeams(true);
      setError('');
      
      // Get teams from public endpoint (no auth required)
      const teamsData = await authService.getTeams();
      console.log('Raw teams data from server:', teamsData);
      
      // Transform server data to match our Team interface
      const transformedTeams: Team[] = teamsData.map((team: any) => ({
        id: String(team.id), // Convert numeric ID to string for UI consistency
        name: team.name,
        passcode: '', // Don't expose passcodes in the frontend
        teamPicture: team.teamPicture
      }));
      
      // Sort teams by ID to ensure consistent ordering
      transformedTeams.sort((a, b) => parseInt(a.id) - parseInt(b.id));
      
      console.log('Received teams from server:', teamsData);
      console.log('Transformed and sorted teams:', transformedTeams);
      
      setTeams(transformedTeams);
    } catch (error: any) {
      console.error('Failed to load teams:', error);
      // Fallback to hardcoded teams if server is unavailable
      const fallbackTeams: Team[] = [
        { id: '1', name: 'Team #1', passcode: '' },
        { id: '2', name: 'Team #2', passcode: '' },
        { id: '3', name: 'Team #3', passcode: '' },
        { id: '4', name: 'Team #4', passcode: '' },
        { id: '5', name: 'Team #5', passcode: '' },
        { id: '6', name: 'Team #6', passcode: '' },
        { id: '7', name: 'Team #7', passcode: '' },
        { id: '8', name: 'Team #8', passcode: '' },
        { id: '9', name: 'Team #9', passcode: '' },
      ];
      setTeams(fallbackTeams);
      setError('Unable to load team data from server. Using offline mode.');
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleTeamSelect = (team: Team) => {
    setSelectedTeam(team);
    setError('');
    setPasscode('');
  };

  const handleBack = () => {
    setSelectedTeam(null);
    setPasscode('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(selectedTeam!.id, passcode);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedTeam) {
    return (
      <>
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>BMD Scavenger Hunt</h1>
            <p>Select your team to continue</p>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="team-selection">
            {loadingTeams ? (
              <div className="loading-teams">
                <p>Loading teams...</p>
              </div>
            ) : (
              <>
                {teams.map((team, index) => (
                  <div
                    key={team.id}
                    onClick={() => handleTeamSelect(team)}
                    className="team-card"
                  >
                    <div className="team-card-picture">
                      {team.teamPicture ? (
                        <img 
                          src={`/api/uploads/${team.teamPicture}`} 
                          alt={team.name}
                          className="team-picture-preview"
                        />
                      ) : (
                        <div className="team-picture-placeholder">
                          👥
                        </div>
                      )}
                    </div>
                    <div className="team-card-info">
                      <div className="team-card-name">Team #{team.id}</div>
                      <div className="team-card-name">{team.name}</div>
                    </div>
                  </div>
                ))}
                
              </>
            )}
          </div>
          <div>
                <div
                  onClick={() => handleTeamSelect({ id: '999', name: 'Huntmaster', passcode: '' })}
                  className="team-card huntmaster-card"
                >
                  
                  <div className="team-card-info">
                    <div className="team-card-id">Huntmaster access</div>                    
                  </div>
                </div>
              </div>
          
          {!loadingTeams && (
            <div className="login-footer">
              <p>Contact your team leader if you're not sure which team you're on.</p>
              <p>💡 Please customize your team name and upload the team picture!</p>
            </div>
          )}
        </div>
        
        
      </div>
      <Rules /></>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>BMD Scavenger Hunt</h1>
          <p>Enter the passcode for <strong>{selectedTeam.id==='999' ? 'Hunt Master' : `Team #${selectedTeam.id}`}</strong></p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="passcode">Team Passcode</label>
            <input
              type="password"
              id="passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Enter your team's passcode"
              required
              disabled={loading}
              className="passcode-input"
              autoFocus
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="button-group">
           
            <button 
              type="submit" 
              disabled={loading || !passcode} 
              className="login-button"
            >
              {loading ? 'Logging in...' : 'Enter Hunt'}
            </button>
             <button 
              type="button" 
              onClick={handleBack}
              className="back-button"
              disabled={loading}
            >
              ← Back to Teams
            </button>
          </div>
        </form>
        
        <div className="login-footer">
          <p>Contact your team leader if you need help with your passcode.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
